import { useState, useEffect } from "react";
import "./CRM.css";
import { useNavigate } from "react-router-dom";
import dataService from "../services/dataService";
import authService from "../services/authService";
import Modal from "../components/Modal";
import { useToast } from "../context/ToastContext";
import { generateInvoicePDF } from "../utils/invoiceGenerator";
import { formatCurrency } from "../utils/formatCurrency";
import {
  Eye,
  FileEdit,
  Trash2,
  MessageCircle,
  Plus,
  Search,
  FileDown,
  CreditCard,
  Download,
} from "lucide-react";

function InvoicesScreen() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [formData, setFormData] = useState({});
  const [paymentFormData, setPaymentFormData] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const navigate = useNavigate();
  const { addToast } = useToast();

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await dataService.getOrders("Invoice");
      setInvoices(response.data.data);
    } catch (error) {
      console.error("Failed to fetch invoices", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const handleCreateInvoice = () => navigate("/invoices/create");

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const rows = text.split('\n').map(row => row.split(','));
      
      const headers = rows[0].map(h => h.trim().replace(/"/g, ''));
      const idIdx = headers.findIndex(h => h.toLowerCase().includes('invoice') || h.toLowerCase().includes('id') || h.toLowerCase().includes('number'));
      const customerIdx = headers.findIndex(h => h.toLowerCase().includes('customer') || h.toLowerCase().includes('client'));
      const dateIdx = headers.findIndex(h => h.toLowerCase().includes('date'));
      const dueDateIdx = headers.findIndex(h => h.toLowerCase().includes('due'));
      const amountIdx = headers.findIndex(h => h.toLowerCase().includes('amount') || h.toLowerCase().includes('total'));
      const productIdx = headers.findIndex(h => h.toLowerCase().includes('product') || h.toLowerCase().includes('item'));
      const qtyIdx = headers.findIndex(h => h.toLowerCase().includes('quantity') || h.toLowerCase().includes('qty'));
      const priceIdx = headers.findIndex(h => h.toLowerCase().includes('price') || h.toLowerCase().includes('rate'));
      const statusIdx = headers.findIndex(h => h.toLowerCase().includes('status'));
      const phoneIdx = headers.findIndex(h => h.toLowerCase().includes('phone') || h.toLowerCase().includes('contact'));

      const newInvoices = [];
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (row.length < 2) continue;
        
        const amountVal = parseFloat((row[amountIdx] || '0').replace(/"/g, '').trim()) || 0;
        const qtyVal = parseInt((row[qtyIdx] || '1').replace(/"/g, '').trim(), 10) || 1;
        const priceVal = parseFloat((row[priceIdx] || '0').replace(/"/g, '').trim()) || 0;
        const prodName = (row[productIdx] || 'General Sale Item').replace(/"/g, '').trim();

        newInvoices.push({
          orderId: (row[idIdx] || `INV-${Date.now().toString().slice(-4)}-${i}`).replace(/"/g, '').trim(),
          customer: (row[customerIdx] || 'Imported Customer').replace(/"/g, '').trim(),
          customerPhone: (row[phoneIdx] || '').replace(/"/g, '').trim(),
          date: (row[dateIdx] || new Date().toISOString().split('T')[0]).replace(/"/g, '').trim(),
          dueDate: (row[dueDateIdx] || new Date().toISOString().split('T')[0]).replace(/"/g, '').trim(),
          amount: amountVal || (qtyVal * priceVal),
          items: [{
            product: prodName,
            quantity: qtyVal,
            price: priceVal || (amountVal / qtyVal),
            total: amountVal || (qtyVal * priceVal)
          }],
          status: (row[statusIdx] || 'Outstanding').replace(/"/g, '').trim(),
          type: "Invoice",
          discount: 0
        });
      }

      if (newInvoices.length > 0) {
        addToast(`Importing ${newInvoices.length} invoices...`, "success");
        try {
          for (const inv of newInvoices) {
            await dataService.addOrder(inv);
          }
          fetchInvoices();
          addToast("Import successful!", "success");
        } catch (err) {
          addToast("Error during import", "error");
        }
      }
    };
    reader.readAsText(file);
  };

  const handleView = (invoice) => {
    setSelectedInvoice(invoice);
    setModalMode("view");
    setIsModalOpen(true);
  };

  const handleEdit = (invoice) => navigate(`/invoices/edit/${invoice._id}`);

  const handleDeleteClick = (invoice) => {
    setItemToDelete(invoice);
    setIsDeleteModalOpen(true);
  };

  // Issue #8 & #11 — Quick Record Payment modal
  const handleRecordPaymentClick = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentFormData({
      paymentId: `PAY-${Math.floor(1000 + Math.random() * 9000)}`,
      invoice: invoice.orderId,
      customer: invoice.customer,
      date: new Date().toISOString().split("T")[0],
      amount: invoice.amount,
      method: "Cash",
      referenceNumber: "",
      notes: "",
      currency: "AED",
      type: "Receive Payment",
      status: "Completed",
    });
    setIsPaymentModalOpen(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setPaymentLoading(true);
    try {
      await dataService.recordPayment(paymentFormData);
      // Also update invoice status to Paid
      await dataService.updateOrder(selectedInvoice._id, {
        paymentStatus: "Paid",
        status: "Paid",
        paymentMethod: paymentFormData.method,
      });
      setIsPaymentModalOpen(false);
      fetchInvoices();
      addToast("Payment recorded and invoice marked as Paid!", "success");
    } catch (err) {
      addToast("Failed to record payment: " + err.message, "error");
    } finally {
      setPaymentLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await dataService.deleteOrder(itemToDelete._id);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      fetchInvoices();
      addToast("Invoice deleted successfully", "success");
    } catch (error) {
      addToast("Failed to delete invoice: " + error.message, "error");
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      await dataService.updateOrder(selectedInvoice._id, formData);
      setIsModalOpen(false);
      fetchInvoices();
      addToast("Invoice updated successfully", "success");
    } catch (error) {
      addToast("Failed to update invoice: " + error.message, "error");
    }
  };

  const handleWhatsAppFollowUp = (invoice) => {
    if (!invoice.customerPhone) {
      addToast("No phone number found for this customer", "error");
      return;
    }
    const phone = invoice.customerPhone.replace(/\D/g, "");
    const message = `Hello, this is a follow-up regarding Invoice #${invoice.orderId} for ${formatCurrency(invoice.amount)}. Please let us know the status of the payment. Thank you!`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, "_blank");
  };

  const paymentMethods = ["Cash", "Bank Transfer", "Online / Card", "Payment Link", "Cheque", "Other"];

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div className="header-info">
          <h1>Invoice Management</h1>
          <p>Create and manage professional invoices</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <label className="secondary-btn" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Download size={18} /> Import CSV
            <input type="file" accept=".csv" onChange={handleImportCSV} style={{ display: 'none' }} />
          </label>
          {authService.hasPermission("invoice", "createInvoice") && (
            <button className="primary-btn" onClick={handleCreateInvoice}>
              <Plus size={18} /> Create Invoice
            </button>
          )}
        </div>
      </div>

      <div className="summary-grid">
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Invoices</span>
            <h2 className="stat-value">{invoices.length}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Invoiced</span>
            <h2 className="stat-value">{formatCurrency(invoices.reduce((acc, inv) => acc + inv.amount, 0))}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Paid</span>
            <h2 className="stat-value" style={{ color: "#00A859" }}>
              {formatCurrency(invoices.filter((inv) => inv.status === "Paid").reduce((acc, inv) => acc + inv.amount, 0))}
            </h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Outstanding</span>
            <h2 className="stat-value" style={{ color: "#DC2626" }}>
              {formatCurrency(invoices.filter((inv) => inv.status === "Outstanding").reduce((acc, inv) => acc + inv.amount, 0))}
            </h2>
          </div>
        </div>
      </div>

      <div className="content-card" style={{ marginTop: "32px" }}>
        <div className="card-controls">
          <h2 className="font-bold">Invoices</h2>
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search invoices by ID, customer name, or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-wrapper">
          {loading ? (
            <p style={{ padding: "20px" }}>Loading invoices...</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Due Date</th>
                  <th>Amount</th>
                  <th>Tax</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th style={{ minWidth: "200px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices
                  .filter(
                    (inv) =>
                      inv.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      (inv.customer && inv.customer.toLowerCase().includes(searchTerm.toLowerCase()))
                  )
                  .map((invoice) => (
                    <tr key={invoice._id}>
                      <td><span className="font-bold">{invoice.orderId}</span></td>
                      <td>{invoice.customer || "N/A"}</td>
                      <td>{new Date(invoice.date).toLocaleDateString()}</td>
                      <td>{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : "N/A"}</td>
                      <td>{formatCurrency(invoice.amount - (invoice.tax || 0))}</td>
                      <td>{formatCurrency(invoice.tax || 0)}</td>
                      <td><span className="font-bold">{formatCurrency(invoice.amount)}</span></td>
                      <td>
                        <span className={`status-pill ${invoice.status.toLowerCase()}`}>{invoice.status}</span>
                      </td>
                      {/* Issue #10 — action buttons always visible */}
                      <td>
                        <div className="action-icons" style={{ display: "flex", gap: "6px", flexWrap: "nowrap", justifyContent: "flex-start" }}>
                          <button className="icon-btn" title="View" onClick={() => handleView(invoice)} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                            <Eye size={16} />
                          </button>

                          <button
                            className="icon-btn"
                            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#16a34a" }}
                            title="Download PDF"
                            onClick={() => generateInvoicePDF(invoice)}
                          >
                            <FileDown size={16} />
                          </button>

                          <button
                            className="icon-btn"
                            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#2E7D32" }}
                            title="WhatsApp Follow-up"
                            onClick={() => handleWhatsAppFollowUp(invoice)}
                          >
                            <MessageCircle size={16} />
                          </button>

                          {/* Issue #8 — Record Payment button */}
                          {invoice.status !== "Paid" && (
                            <button
                              className="icon-btn"
                              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#7C3AED" }}
                              title="Record Payment"
                              onClick={() => handleRecordPaymentClick(invoice)}
                            >
                              <CreditCard size={16} />
                            </button>
                          )}

                          {authService.hasPermission("invoice", "editInvoice") && (
                            <button
                              className="icon-btn success"
                              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                              title="Edit"
                              onClick={() => handleEdit(invoice)}
                            >
                              <FileEdit size={16} />
                            </button>
                          )}

                          {authService.hasPermission("invoice", "deleteInvoice") && (
                            <button
                              className="icon-btn danger"
                              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                              title="Delete"
                              onClick={() => handleDeleteClick(invoice)}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                {invoices.length === 0 && (
                  <tr>
                    <td colSpan="9" style={{ textAlign: "center", padding: "40px" }}>No invoices found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* View / Edit Invoice Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalMode === "view" ? "Invoice Details" : "Edit Invoice"}
        footer={
          modalMode === "edit" ? (
            <>
              <button className="secondary-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="primary-btn" onClick={handleUpdateSubmit}>Save Changes</button>
            </>
          ) : (
            <div style={{ display: "flex", gap: "10px", width: "100%", justifyContent: "flex-end" }}>
              <button className="secondary-btn" onClick={() => generateInvoicePDF(selectedInvoice)}>
                <FileDown size={18} style={{ marginRight: "8px" }} /> Download PDF
              </button>
              <button className="primary-btn" onClick={() => setIsModalOpen(false)}>Close</button>
            </div>
          )
        }
      >
        {modalMode === "view" && selectedInvoice && (
          <div className="detail-view">
            <div className="detail-item"><span className="detail-label">Invoice Number</span><span className="detail-value">{selectedInvoice.orderId}</span></div>
            <div className="detail-item"><span className="detail-label">Customer</span><span className="detail-value">{selectedInvoice.customer}</span></div>
            <div className="detail-item"><span className="detail-label">Address</span><span className="detail-value">{selectedInvoice.shippingAddress || "N/A"}</span></div>
            <div className="detail-item"><span className="detail-label">Date</span><span className="detail-value">{new Date(selectedInvoice.date).toLocaleDateString()}</span></div>
            <div className="detail-item"><span className="detail-label">Due Date</span><span className="detail-value">{selectedInvoice.dueDate ? new Date(selectedInvoice.dueDate).toLocaleDateString() : "N/A"}</span></div>
            <div className="detail-item">
              <span className="detail-label">Items</span>
              <div className="detail-value">
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {selectedInvoice.items?.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: "8px", paddingBottom: "8px", borderBottom: "1px solid #F3F4F6" }}>
                      {item.product} {item.description ? `— ${item.description}` : ""} — {item.quantity} x {formatCurrency(item.price)} = {formatCurrency(item.total)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="detail-item"><span className="detail-label">Tax</span><span className="detail-value">{formatCurrency(selectedInvoice.tax || 0)}</span></div>
            <div className="detail-item"><span className="detail-label">Total Amount</span><span className="detail-value" style={{ fontSize: "20px", fontWeight: "bold" }}>{formatCurrency(selectedInvoice.amount)}</span></div>
            <div className="detail-item"><span className="detail-label">Payment Method</span><span className="detail-value">{selectedInvoice.paymentMethod || "N/A"}</span></div>
            <div className="detail-item"><span className="detail-label">Status</span><span className={`status-pill ${selectedInvoice.status?.toLowerCase()}`}>{selectedInvoice.status}</span></div>
          </div>
        )}

        {modalMode === "edit" && (
          <form className="modal-form" onSubmit={handleUpdateSubmit}>
            <div className="form-group">
              <label>Invoice Number</label>
              <input name="orderId" value={formData.orderId || ""} readOnly disabled />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={formData.status || ""} onChange={handleFormChange}>
                <option value="Outstanding">Outstanding</option>
                <option value="Paid">Paid</option>
                <option value="Overdue">Overdue</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            {/* Issue #11 — Payment method in edit modal */}
            <div className="form-group">
              <label>Payment Method</label>
              <select name="paymentMethod" value={formData.paymentMethod || "Bank Transfer"} onChange={handleFormChange}>
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Online / Card">Online / Card</option>
                <option value="Payment Link">Payment Link</option>
                <option value="Cheque">Cheque</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Payment Status</label>
              <select name="paymentStatus" value={formData.paymentStatus || "UnPaid"} onChange={handleFormChange}>
                <option value="UnPaid">UnPaid</option>
                <option value="PartiallyPaid">Partially Paid</option>
                <option value="Paid">Paid</option>
              </select>
            </div>
            <div className="form-group">
              <label>Due Date</label>
              <input type="date" name="dueDate" value={formData.dueDate ? formData.dueDate.substring(0, 10) : ""} onChange={handleFormChange} />
            </div>
            <div className="form-group">
              <label>Internal Notes</label>
              <textarea name="internalNotes" value={formData.internalNotes || ""} onChange={handleFormChange} rows={2} style={{ width: "100%" }} />
            </div>
          </form>
        )}
      </Modal>

      {/* Issue #8 — Quick Record Payment Modal */}
      <Modal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        title={`Record Payment — ${selectedInvoice?.orderId}`}
        footer={
          <>
            <button className="secondary-btn" onClick={() => setIsPaymentModalOpen(false)}>Cancel</button>
            <button className="primary-btn" onClick={handlePaymentSubmit} disabled={paymentLoading}>
              {paymentLoading ? "Saving..." : "Record Payment"}
            </button>
          </>
        }
      >
        <form className="modal-form" onSubmit={handlePaymentSubmit}>
          <div className="form-group">
            <label>Customer</label>
            <input value={paymentFormData.customer || ""} disabled />
          </div>
          <div className="form-group">
            <label>Invoice Amount</label>
            <input type="number" value={paymentFormData.amount || 0} onChange={(e) => setPaymentFormData({ ...paymentFormData, amount: parseFloat(e.target.value) || 0 })} required />
          </div>
          <div className="form-group">
            <label>Payment Date</label>
            <input type="date" value={paymentFormData.date || ""} onChange={(e) => setPaymentFormData({ ...paymentFormData, date: e.target.value })} required />
          </div>
          {/* Issue #8 — Payment method options */}
          <div className="form-group">
            <label>Payment Method</label>
            <select value={paymentFormData.method || "Cash"} onChange={(e) => setPaymentFormData({ ...paymentFormData, method: e.target.value })}>
              {paymentMethods.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Reference Number (optional)</label>
            <input value={paymentFormData.referenceNumber || ""} onChange={(e) => setPaymentFormData({ ...paymentFormData, referenceNumber: e.target.value })} placeholder="TRX-xxxx" />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea value={paymentFormData.notes || ""} onChange={(e) => setPaymentFormData({ ...paymentFormData, notes: e.target.value })} rows={2} style={{ width: "100%" }} />
          </div>
        </form>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirm Deletion"
        footer={
          <>
            <button className="secondary-btn" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
            <button className="primary-btn btn-danger" onClick={confirmDelete}>Delete Invoice</button>
          </>
        }
      >
        <div className="delete-confirm-view">
          <div className="delete-icon-wrapper">
            <Trash2 size={32} />
          </div>
          <h3>Are you sure?</h3>
          <p>You are about to delete invoice <strong>{itemToDelete?.orderId}</strong>. This action cannot be undone.</p>
        </div>
      </Modal>
    </div>
  );
}

export default InvoicesScreen;